import { ArrowLeft, Check, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { recordNewAuthorityExpertRequest } from '@/models/funnelSession'
import { CarrierResultCard } from '@/routes/Public/CarrierResultCard'
import { eligibleCarrierNames, genericEstimateCards } from '@/routes/Public/newAuthorityEstimates'
import { TalkToExpertDialog } from '@/routes/Public/TalkToExpertDialog'
import { withRangeMeta } from './getEstimatesData'

function carrierListText(names: string[]): string {
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return names.join(', ')
}

/**
 * The no-DOT "new authority" branch, kept as its own panel because it's a
 * genuinely different flow — a single CDL question, then ballpark ranges (no
 * carrier lookup, no application). Rendered in place of the two-column DOT
 * layout when the visitor taps "Don't have a DOT yet?".
 */
export function NewAuthorityPanel({ onBack }: { onBack: () => void }) {
  const [hasTwoYearCdl, setHasTwoYearCdl] = useState<boolean | null>(null)
  const [expertOpen, setExpertOpen] = useState(false)

  return (
    <div className="mx-auto w-full max-w-2xl">
      <Button variant="link" className="mb-4 self-start pl-0" onClick={onBack}>
        <ArrowLeft className="size-4" />
        Back to DOT entry
      </Button>

      {hasTwoYearCdl === null ? (
        <CdlQuestion onAnswer={setHasTwoYearCdl} />
      ) : (
        <Estimates
          hasTwoYearCdl={hasTwoYearCdl}
          onChangeAnswer={() => setHasTwoYearCdl(null)}
          onTalkToExpert={() => setExpertOpen(true)}
        />
      )}

      <TalkToExpertDialog
        open={expertOpen}
        onOpenChange={setExpertOpen}
        submit={(c) => recordNewAuthorityExpertRequest({ ...c, hasTwoYearCdl: hasTwoYearCdl ?? false })}
      />
    </div>
  )
}

function CdlQuestion({ onAnswer }: { onAnswer: (hasTwoYearCdl: boolean) => void }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <span className="border-cai-blue/30 bg-cai-blue/5 text-cai-blue self-start rounded-full border px-4 py-1.5 text-sm font-semibold">
          New Authority · Question 1 of 1
        </span>
        <h2 className="text-foreground text-2xl font-bold md:text-3xl">
          Do your drivers have 2+ years of CDL driving experience?
        </h2>
      </div>

      <div className="mt-2 flex flex-col gap-3">
        <CdlOption
          tone="positive"
          title="Yes — 2 or more years"
          subtitle={`${eligibleCarrierNames(true).length} carriers available: ${carrierListText(eligibleCarrierNames(true))}`}
          onClick={() => onAnswer(true)}
        />
        <CdlOption
          tone="caution"
          title="No — less than 2 years"
          subtitle={`${eligibleCarrierNames(false).length} carriers available: ${carrierListText(eligibleCarrierNames(false))}`}
          onClick={() => onAnswer(false)}
        />
      </div>
    </div>
  )
}

function CdlOption({
  tone,
  title,
  subtitle,
  onClick,
}: {
  tone: 'positive' | 'caution'
  title: string
  subtitle: string
  onClick: () => void
}) {
  const positive = tone === 'positive'
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className="hover:border-cai-blue flex h-auto w-full items-start justify-start gap-4 rounded-xl border p-5 text-left whitespace-normal"
    >
      <div
        className={cn(
          'flex size-11 shrink-0 items-center justify-center rounded-full',
          positive ? 'bg-accent-green/15 text-accent-green' : 'bg-warning/40 text-warning-d1'
        )}
      >
        {positive ? <Check className="size-5" strokeWidth={3} /> : <TriangleAlert className="size-5" />}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-foreground text-lg font-bold">{title}</span>
        <span className="text-muted-foreground text-sm">{subtitle}</span>
      </div>
    </Button>
  )
}

function Estimates({
  hasTwoYearCdl,
  onChangeAnswer,
  onTalkToExpert,
}: {
  hasTwoYearCdl: boolean
  onChangeAnswer: () => void
  onTalkToExpert: () => void
}) {
  const { cards, rangeBounds } = withRangeMeta(genericEstimateCards(hasTwoYearCdl))

  return (
    <div className="flex flex-col">
      <header className="mb-6 flex flex-col gap-2">
        <div className="text-accent-green text-sm font-semibold uppercase">
          {cards.length} {cards.length === 1 ? 'carrier' : 'carriers'} available
        </div>
        <h2 className="text-xl font-semibold">Ballpark rates for a new authority</h2>
      </header>

      <div className="flex flex-col gap-3">
        {cards.map((card) => (
          <CarrierResultCard key={card.id} card={card} rangeBounds={rangeBounds} />
        ))}
      </div>

      <p className="text-muted-foreground mt-6 text-xs">
        Ballpark ranges for a brand-new authority — not carrier quotes. Real pricing depends on your operation
        and safety history once you have a DOT number and active authority.
      </p>

      <div className="mt-8 flex w-full max-w-sm flex-col gap-3 self-center pb-4">
        <Button className="w-full" onClick={onTalkToExpert}>
          Talk to an expert
        </Button>
        <Button variant="ghost" className="w-full" onClick={onChangeAnswer}>
          Change answer
        </Button>
      </div>
    </div>
  )
}
